<script setup>
import {computed, onBeforeMount, ref} from "vue";
import axios from "axios";
import {find, flatMap, map} from "lodash";
import {useStore} from "vuex";
import FlowGroup from '@/components/Dashboard/FlowGroup.vue';
import DataManipulationBlock from '@/components/Dashboard/DataManipulationBlock.vue';
import ManualLinkButton from '@/components/Shared/ManualLinkButton.vue';
import FlowLoadTemplateModal from "@/Pages/Dashboard/Integrations/Flows/Partials/FlowLoadTemplateModal.vue";

defineProps({manualLink: String})

const initialLoading = ref(true)
const dataManipulationOptions = ref([])
const showLoadTemplateModal = ref(false)

const store = useStore()

const flowGroup = computed(() => store.getters["flowsDetails/flowGroup"])
const userIntegration = computed(() => store.getters["flowsDetails/userIntegration"])
const userIntegrationFlow = computed(() => store.getters["flowsDetails/userIntegrationFlow"])
const isAdmin = computed(() => store.getters["flowsDetails/isAdmin"])

const totalStoredBusinessRules = computed(() => dataManipulationOptions.value.filter((option) => option.id).length)

onBeforeMount(() => {
  axios
    .get(`/api/integrations/${userIntegration.value.id}/flows/${userIntegrationFlow.value.id}/business-rules`)
    .then(({data}) => {
      store.dispatch("flowsDetails/setDatamodelFields", data.data.datamodelFields)
      store.dispatch("flowsDetails/setConditions", data.data.conditions)
      store.dispatch("flowsDetails/setActions", data.data.actions)
      store.dispatch("flowsDetails/setActionsNotMapped", data.data.actionsNotMapped)
      store.dispatch("flowsDetails/setOperands", data.data.operands)
      store.dispatch("flowsDetails/setStarts", data.data.starts)
      store.dispatch("flowsDetails/setEnds", data.data.ends)
      store.dispatch("flowsDetails/setUseDefaultFor", data.data.useDefaultFor)
      store.dispatch("flowsDetails/setMicrofunctions", data.data.microfunctions)
      store.dispatch("flowsDetails/setApplyActionTo", data.data.applyActionTo)
      store.dispatch("flowsDetails/setUseMicrofunctionWhen", data.data.useMicrofunctionWhen)
      store.dispatch("flowsDetails/setExecuteRuleWhen", data.data.executeRuleWhen)
      store.dispatch("flowsDetails/setBlockRules", data.data.blockRules)
      const actions = flatMap(data.data.actions, 'options')

      dataManipulationOptions.value = map(data.data.dataManipulationOptions, option => {
        option.conditional_tables = map(option.conditional_tables, table => {
          return map(table, row => {
            row.action = find(actions, actionOption => actionOption.value === row.action)

            return row
          });
        });

        return option
      });
    })
    .finally(() => initialLoading.value = false)
})

const handleDeletedBusinessRule = (index) => dataManipulationOptions.value.splice(index, 1)
const addBusinessRule = () => dataManipulationOptions.value.push({
  id: null,
  datamodel_field: null,
  alias: null,
  rule_enabled: true,
  default_usage: false,
  default_forced: false,
  default_value: null,
  use_mapping_table: false,
  use_conditional_mapping_table: false,
  microfunction_usage: false,
  microfunction: null,
  use_microfunction_when: 0,
  execute_rule_when: 2,
})

const handleBusinessRuleMove = (direction, index) => {
  let businessRule = dataManipulationOptions.value[index]

  axios
    .patch(`/api/integrations/${userIntegration.value.id}/flows/${userIntegrationFlow.value.id}/business-rules/${businessRule.id}/move/${direction}`)
    .then(() => {
      const from = index
      const to = index + (direction === 'up' ? -1 : 1)

      dataManipulationOptions.value.splice(to, 0, dataManipulationOptions.value.splice(from, 1)[0])
    })
}
</script>
<template>
  <div>
    <div class="tile my-4 px-3 py-4">
      <flow-group :flowGroup="flowGroup"/>
    </div>

    <div class="tile my-4">
      <loading-view :loading="initialLoading">
        <form>
          <div v-for="(dataManipulationOption, index) in dataManipulationOptions" :key="dataManipulationOption.id">
            <data-manipulation-block
              v-model="dataManipulationOptions[index]"
              :can-move-up="dataManipulationOption.id !== null && index > 0"
              :can-move-down="dataManipulationOption.id !== null && totalStoredBusinessRules > (index + 1)"
              @deleted="handleDeletedBusinessRule(index)"
              @move="(direction) => handleBusinessRuleMove(direction, index)"
            />
          </div>
        </form>

        <div class="flex mt-4">
          <manual-link-button :manual-link="manualLink"/>
          <button
            v-if="$can('flows.business-rules.create')"
            class="button button_success"
            type="button"
            @click="addBusinessRule"
          >
            {{ trans('Add Business Rule Block') }}
          </button>

          <button
            v-if="isAdmin"
            class="button"
            type="button"
            v-text="trans('Load from template')"
            @click="showLoadTemplateModal = true"
          />
        </div>

        <flow-load-template-modal
          v-if="isAdmin"
          :show="showLoadTemplateModal"
          :user-integration-id="userIntegration.id"
          :user-integration-flow-id="userIntegrationFlow.id"
          type="business_rule"
          @close="showLoadTemplateModal = false"
        />
      </loading-view>
    </div>
  </div>
</template>
<script>
import DashboardLayout from '@/Layouts/DashboardLayout.vue';
import IntegrationFlowLayout from '@/Layouts/IntegrationFlowLayout.vue';

export default {
  name: 'FlowBusinessRules',

  layout: [DashboardLayout, IntegrationFlowLayout],
};
</script>
